import React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Check from '@mui/icons-material/Check';
import { DebtorIndex } from '../../../api/client';
import { formatNationalIdentityNumber, formatPhoneNumber } from '../../Utils';
import { DebtOverview } from '../../debtOverview/DebtOverview';
import DebtorIcon from '../../debtorOverview/presentation/DebtorIcon';
import currencyFormatter from 'currency-formatter';
import Link from '@mui/material/Link';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { parsePhoneNumber } from 'awesome-phonenumber';

interface Props {
  debtor: DebtorIndex | null;
  outstanding: number;
}

const getCdmPath = (system: string | undefined, id: string | undefined) => {
  if (system === undefined || id === undefined || system !== 'leap') {
    return '';
  }
  var path: string = `${process.env.REACT_APP_CDM_LEAP_PROVIDER_ID_URL_PROD}/${id}`;
  const hostname = window.location.hostname;
  if (hostname.startsWith('conveneportal.dev')) {
    path = `${process.env.REACT_APP_CDM_LEAP_PROVIDER_ID_URL_DEV}/${id}`;
  }

  if (hostname.startsWith('conveneportal.qa') || hostname === 'localhost') {
    path = `${process.env.REACT_APP_CDM_LEAP_PROVIDER_ID_URL_STG}/${id}`;
  }

  return (<Link
    href={path}
    rel="noreferrer"
    target="_blank"
  >
    <OpenInNew sx={{
      fontSize: '25px',
      marginTop: '3px',
      marginLeft: '6px',
      width: '1em',
      height: '1em'
    }} />
  </Link>);
};

export default function Debtor(props: Props) {
  const [copied, setCopied] = React.useState(false);
  const [copiedPhoneIndex, setCopiedPhoneIndex] = React.useState<number | null>(null);
  const [copiedEmail, setCopiedEmail] = React.useState(false);

  const handleCopy = () => {
    const value = props.debtor?.isOrganization
      ? props.debtor.organizationNumber
      : formatNationalIdentityNumber(props.debtor?.nationalIdentityNumber) || props.debtor?.dateOfBirth;
    const text = (value ?? '').toString().replace(/\s+/g, '');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleCopyPhone = (phoneNumber: string, index: number) => {
    const significant = parsePhoneNumber(phoneNumber, { regionCode: 'NO' }).number?.significant ?? phoneNumber.replace(/\s+/g, '');
    navigator.clipboard.writeText(significant);
    setCopiedPhoneIndex(index);
    setTimeout(() => setCopiedPhoneIndex(null), 1500);
  };

  const handleCopyEmail = () => {
    const text = (props.debtor?.email ?? '').trim();
    navigator.clipboard.writeText(text);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 1500);
  };

  return props.debtor ? (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      maxWidth: '1000px',
      minWidth: '700px',
      margin: '0 auto',
    }}>
      <Box sx={{
        marginBottom: '14px',
        display: 'flex',
        flexDirection: 'row',
        //alignItems: 'start',
        justifyContent: 'space-between'
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'start',
        }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            <DebtorIcon isOrganization={props.debtor.isOrganization} /> {props.debtor.name}
          </Typography>
          {getCdmPath(props.debtor.system, props.debtor.externalReferenceContactId)}
          <Typography variant="h5" sx={{ marginLeft: '5px', textWrap: 'nowrap' }}>
            {props.debtor.isOrganization ? props.debtor.organizationNumber : formatNationalIdentityNumber(props.debtor.nationalIdentityNumber) || props.debtor.dateOfBirth}
          </Typography>
          <Tooltip title={copied ? 'Kopiert!' : 'Kopier'}
            disableInteractive
            componentsProps={{
              tooltip: {
                sx: { fontSize: '0.80rem' }
              }
            }}>
            <IconButton
              size="small"
              onClick={handleCopy}
              disableRipple
              sx={{ marginLeft: '5px', marginTop: '2px', width: 28, height: 28 }}
            >
              {copied ? <Check fontSize="small" sx={{ color: 'success.main' }} /> : <ContentCopy fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'start',
          marginLeft: '4px'
        }}>
          <DebtOverview />
        </Box>
      </Box>
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        minHeight: '150px'
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
        }}>
          {props.debtor.extendedAddressLines?.map((item, i) =>
              i <= 2 && ( 
                <Typography key={i}>
                  {item}
                </Typography>
              )
          )}

          {props.debtor.extendedAddressLines && props.debtor.extendedAddressLines.length > 3 &&
            <Typography>
              {props.debtor.extendedAddressLines?.map((item, i) =>
                i > 2 && (
                  <>{item} </>
                )
              )}
            </Typography>
          }

          <Typography>
            {props.debtor.street}
          </Typography>
          <Typography>
            {props.debtor.postCode} {props.debtor.postArea}
          </Typography>
          <Typography>
            {props.debtor.country}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'right',
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'right'
          }}>
            {props.debtor.phoneNumbers && [...new Set(props.debtor.phoneNumbers)].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Typography>{formatPhoneNumber(item)}</Typography>
                <Tooltip title={copiedPhoneIndex === i ? 'Kopiert!' : 'Kopier'}
                  disableInteractive
                  componentsProps={{
                    tooltip: {
                      sx: { fontSize: '0.80rem' }
                    }
                  }}>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyPhone(item, i)}
                    disableRipple
                    sx={{ marginLeft: '4px', width: 28, height: 28, alignSelf: 'center', padding: 0 }}
                  >
                    {copiedPhoneIndex === i ? <Check fontSize="small" sx={{ color: 'success.main' }} /> : <ContentCopy fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
            {props.debtor.email && (
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Typography>
                  {props.debtor.email}
                </Typography>
                <Tooltip title={copiedEmail ? 'Kopiert!' : 'Kopier'}
                  disableInteractive
                  componentsProps={{
                    tooltip: {
                      sx: { fontSize: '0.80rem' }
                    }
                  }}>
                  <IconButton
                    size="small"
                    onClick={handleCopyEmail}
                    disableRipple
                    sx={{ marginLeft: '4px', width: 28, height: 28, alignSelf: 'center', padding: 0 }}
                  >
                    {copiedEmail ? <Check fontSize="small" sx={{ color: 'success.main' }} /> : <ContentCopy fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            textAlign: 'right',
            marginTop: 'auto',
          }}>
            <Typography variant="h5" style={{ float: 'right' }}>
              Totalt utestående: <Box sx={{
                display: 'inline',
                ...(props.outstanding === 0 && {
                  color: 'secondary.dark'
                }), ...(props.outstanding !== 0 && {
                  color: 'error.main',
                  fontWeight: 'bold'
                }),
              }}
              >
                {currencyFormatter.format(props.outstanding, { code: 'NOK' })}
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
  : (
    <Box sx={{
      extend: 'root',
      textAlign: 'center',
    }}>
      <Typography variant="h5">
        Kunne ikke hente debitor data
      </Typography>
      <DebtOverview />
    </Box>
  )
};
